import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle, AlertCircle, TrendingUp, Users, Award, Clock, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { TestimonialSlider } from '../../components/TestimonialSlider';
import { ProcessSlider } from '../../components/ProcessSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { faqs } from '../../mock';
import { useScrollAnimation, useCounterAnimation } from '../../hooks/useScrollAnimation';
import { standardServicePricing } from '../../pricingData';
import { PricingSection } from '../../components/PricingSection';

const StatCard = ({ icon: Icon, value, label, delay }) => {
  const [ref, count] = useCounterAnimation(parseInt(value), 2000);
  return (
    <div ref={ref} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all" style={{ animationDelay: `${delay}ms` }}>
      <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="text-orange-600" size={32} />
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-2">{count}+</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
};

export const DueDiligence = ({ onOpenPopup }) => {
  const [benefitsRef, benefitsVisible] = useScrollAnimation(0.2);
  const [statsRef, statsVisible] = useScrollAnimation(0.2);

  return (
    <div className="pt-20">
      <Helmet>
        <title>Due Diligence Process | Professional Legal Services</title>
        <meta name="description" content="Conduct thorough due diligence for informed business decisions and risk mitigation" />
      </Helmet>

      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 left-1/4 animate-float"><Shield className="w-20 h-20 text-white/10" /></div>
          <div className="absolute bottom-32 right-1/3 animate-float" style={{ animationDelay: '1s' }}><Award className="w-24 h-24 text-white/10" /></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 animate-slide-in">
              <span className="text-sm font-semibold">⭐ Professional Service</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-in">Due Diligence Process</h1>
            <p className="text-xl mb-8 text-white/95 leading-relaxed max-w-3xl mx-auto animate-slide-in">Conduct thorough due diligence for informed business decisions and risk mitigation</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-in">
              <Button onClick={onOpenPopup} className="bg-white text-orange-600 hover:bg-gray-100 h-12 px-8 text-base hover:scale-105 transition-all">Get Started Today</Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 h-12 px-8 text-base" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>Live Chat</Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      

      <section className="py-20 bg-gradient-to-br from-orange-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img src="https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800&h=600&fit=crop" alt="DueDiligence service" className="w-full h-96 object-cover"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/80 to-transparent flex items-end p-8">
                      <div className="text-white">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg"><Shield size={32} /></div>
                          <div><div className="text-2xl font-bold">Expert Guidance</div><div className="text-white/90">Professional Support</div></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">📚 Learn More</div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">What is Due Diligence Process?</h2>
                <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>Due diligence is a comprehensive investigation and analysis conducted before business transactions to assess risks evaluate opportunities and verify claims. It's essential for mergers acquisitions investments and major business decisions.</p>
                  <p>Our due diligence services examine financial legal operational and commercial aspects of target companies. We identify risks liabilities and opportunities providing detailed reports to support informed decision-making.</p>
                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-orange-600">
                    <p className="font-semibold text-gray-900 mb-2">💡 Did You Know?</p>
                    <p className="text-gray-700">Professional legal services ensure compliance and protect your business interests throughout the entire process.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose This Service?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Professional guidance and comprehensive support for your legal needs</p>
          </div>
          <div ref={benefitsRef} className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto stagger-children ${benefitsVisible ? 'visible' : ''}`}>
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">1</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Financial Analysis</h3>
              <p className="text-base text-gray-600 leading-relaxed">Comprehensive review of financial statements tax returns and accounting practices to assess financial health and identify risks.</p>
            </div>
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">2</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <CheckCircle className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Legal Review</h3>
              <p className="text-base text-gray-600 leading-relaxed">Examination of contracts liabilities litigation and intellectual property to uncover legal risks and compliance issues.</p>
            </div>
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">3</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <AlertCircle className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Operational Assessment</h3>
              <p className="text-base text-gray-600 leading-relaxed">Analysis of business operations management systems and key processes to evaluate operational efficiency and risks.</p>
            </div>
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">4</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Risk Identification</h3>
              <p className="text-base text-gray-600 leading-relaxed">Identify material risks liabilities and deal-breakers before committing to transactions saving time and money.</p>
            </div>
          </div>
        </div>
      </section>

      

      <ProcessSlider />
      
      
      <div id="pricing"><PricingSection pricingData={standardServicePricing} onOpenPopup={onOpenPopup} /></div>
      <section className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Professional Due Diligence Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">Our expert team provides comprehensive due diligence solutions tailored to your specific business needs and legal requirements.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-orange-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Consultation</h3>
                <p className="text-gray-600 leading-relaxed">Personalized consultation with experienced professionals who understand your industry.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-orange-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Document Preparation</h3>
                <p className="text-gray-600 leading-relaxed">Complete preparation of all necessary legal documents and filings.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-orange-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance Assurance</h3>
                <p className="text-gray-600 leading-relaxed">Ensure all work meets federal, state, and local legal requirements.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-orange-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                <p className="text-gray-600 leading-relaxed">Continued support and guidance throughout the entire process.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-orange-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent Process</h3>
                <p className="text-gray-600 leading-relaxed">Clear communication and updates at every stage of your service.</p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <div className="bg-orange-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="text-orange-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Timely Delivery</h3>
                <p className="text-gray-600 leading-relaxed">Efficient processing with adherence to all deadlines and timeframes.</p>
              </div>
            </div>
            
            <div className="mt-16 bg-orange-600 rounded-3xl p-12 text-center text-white">
              <h3 className="text-3xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                Contact us today to discuss your needs and learn how we can help your business succeed.
              </p>
              <Button 
                size="lg" 
                onClick={onOpenPopup}
                className="bg-white text-orange-600 hover:bg-orange-50 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              >
                Schedule a Consultation →
              </Button>
            </div>
          </div>
        </div>
      </section>


      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">❓ Got Questions?</div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Everything you need to know about our services</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-gradient-to-r from-orange-50 to-orange-50 rounded-2xl px-6 border-none shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-primary py-6">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-base text-gray-600 pb-6 leading-relaxed">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <TestimonialSlider />

      <section className="relative py-20 bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">Join thousands of satisfied clients who trust our professional services</p>
          <Button onClick={onOpenPopup} className="bg-white text-orange-600 hover:bg-gray-100 h-14 px-10 text-lg hover:scale-105 transition-all shadow-xl">
            Start Your Application Today
          </Button>
        </div>
      </section>
    </div>
  );
};
